import {
  IconCalendar3,
  IconCheckedSquare,
  IconClose,
  IconDeliveryFilled,
  IconDeliveryLine,
  IconEdit3,
  IconPartiallyDelivered,
  IconVerticalBarChartFilled,
  IconVerticalBarChartLine,
} from '@odekoteam/doppio';
import Box from 'assets/svgs/box.svg';
import IssueSelected from 'assets/svgs/issueSelected.svg';
import IssueUnselected from 'assets/svgs/issueUnselected.svg';
import MapPinSelected from 'assets/svgs/mapPinSelected.svg';
import MapPinUnselected from 'assets/svgs/mapPinUnselected.svg';
import OdekoIcon from 'assets/svgs/odekoDripIcon.svg';
import Search from 'assets/svgs/search.svg';
import SearchFilled from 'assets/svgs/searchFilled.svg';
import WarehouseNotes from 'assets/svgs/secondaryStopStatus/WarehouseNotes.svg';
import TuningControls from 'assets/svgs/tuningControls.svg';
import WarehouseOperationsSelected from 'assets/svgs/warehouseOperationsSelected.svg';
import WarehouseOperationsUnselected from 'assets/svgs/warehouseOperationsUnselected.svg';
import WarningOctagon from 'assets/svgs/warningOctagon.svg';
import WarningOctagonFilled from 'assets/svgs/warningOctagonFilled.svg';
import { useAuth0 } from '@auth0/auth0-react';
import { useBreakpointIndex } from '@theme-ui/match-media';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLayoutEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { useOutboundGatesEnabled } from 'src/hooks/useOutboundGatesEnabled';
import { useWarehouseLayoutBuilderEnabled } from 'src/hooks/useWarehouseLayoutBuilderEnabled';
import { isNavCollapsedSelector } from 'src/state/selectors/app';
import { selectedWarehouseIdSelector } from 'src/state/selectors/delivery';
import { collapseNav } from 'src/state/slices/app';
import { hasPutawayAppAccess, isPutawayOnlyUser } from 'src/utils/authRoles';
import { isWarehouseFlagEnabled } from 'src/utils/warehouseFlag';
import HelpCenter from './HelpCenter';
import Logout from './Logout';
import NavLink from './NavLink';
import {
  backButtonStyle,
  exitContainer,
  header,
  headerContainerStyle,
  innerContainerStyle,
  issuesSymbol,
  menuContainerStyle,
  notMenuStyle,
  page,
  phoneMenuContainerStyle,
  topBarStyle,
  warehouseOperationsSymbol,
} from './styles';

export const deliveryPath = '/deliveries';
export const loginPath = '/login';
export const analyticsPath = '/analytics';
export const issuesPath = '/issues';
export const invoiceByDeliveryPath = '/invoice-by-delivery';
export const invoiceByDeliveryDetailPath = '/invoice-by-delivery/:orderId';
export const getInvoiceByDeliveryDetailPath = (orderId: string): string =>
  `${invoiceByDeliveryPath}/${orderId}`;
export const locationsPath = '/locations';
export const warehouseOperationsPath = '/warehouse-operations';
export const optimizationPath = '/optimization';
export const optimizationNewPath = `${optimizationPath}/new`;
export const optimizationComparePath = `${optimizationPath}/compare`;
export const putawayReportPath = '/putaway-report';
export const putawayAppPath = '/putaway-app';
// Child of putawayAppPath so it stays reachable inside the PWA home-lock.
export const putawaySettingsPath = `${putawayAppPath}/settings`;
export const warehouseLayoutPath = '/warehouse-layout';
export const warehouseLayoutBuilderPath = `${warehouseLayoutPath}/builder`;
export const pickingLabelsPath = '/picking-labels';
export const fulfillmentDeltasPath = '/fulfillment-deltas';
export const fulfillmentDeltasPrintablePath = '/fulfillment-deltas-printable';
export const itemSearchPath = '/item-search';
export const packingListPath = '/packing-list';
export const deliveryNotesPath = '/delivery-notes';
export const driverSafetyAcknowledgementsPath = '/acknowledgements';
export const driverSafetyAcknowledgementsCreatePath = '/acknowledgements/new';
export const vehicleInspectionReportsPath = '/vehicle-inspection-reports';
export const vehicleInspectionTruckPath = `${vehicleInspectionReportsPath}/trucks/:truckId`;
export const vehicleInspectionReportPath = `${vehicleInspectionReportsPath}/reports/:reportId`;
export const getVehicleInspectionTruckPath = (truckId: string): string =>
  `${vehicleInspectionReportsPath}/trucks/${truckId}`;
export const getVehicleInspectionReportPath = (reportId: string): string =>
  `${vehicleInspectionReportsPath}/reports/${reportId}`;
export const vendorReceivingPath = '/vendor-receiving';
export const stageGatePath = '/stage-gate';
export const dockBookingsPath = '/dock-bookings';
// Public (unauthenticated) dock-booking pages — external users, no nav links.
// One static link shared with all carriers; the PO is the lookup key.
export const dockBookingPublicPath = '/inbound-appointment';
export const dockDisplayPublicPath = '/docks/:dockId/display';
// Public (unauthenticated) driver check-in kiosk — a fullscreen wizard on a
// warehouse tablet. dockId labels the welcome screen when a kiosk is pinned to
// a specific dock (multiple physical kiosks per warehouse), and is also sent
// to the check-in API to confirm the driver is checking in at the right
// warehouse.
export const dockCheckinPublicPath = '/dock-checkin/:dockId?';
// Warehouse-wide check-in entry point for the printed placard: no physical
// kiosk is pinned to a specific dock at the placard's posting location, so it
// carries the warehouse instead — same role as dockDepartedWarehousePublicPath
// below, for the check-in side.
export const dockCheckinWarehousePublicPath =
  '/dock-checkin/warehouse/:warehouseId';
// Public (unauthenticated) ops "Departed" screen — same tablet as the driver
// kiosk, a different screen, mounted at one physical dock. dockId is
// required: the whole point of this screen is showing that one door's own
// occupant.
export const dockDepartedPublicPath = '/docks/:dockId/departed';
// Warehouse-wide fallback for the screen above, for when a specific door's
// kiosk is down and ops needs to depart a truck from a different one.
export const dockDepartedWarehousePublicPath =
  '/docks/warehouse/:warehouseId/departed';
// Public (unauthenticated) portal for non-inventory vendors: they sign in with
// their issued override code (handed off from the carrier gate via router state,
// never the URL) to list/book/change their own warehouse deliveries — no PO/TO.
export const vendorPortalPublicPath = '/vendor-portal';
export const processPathsPath = '/process-paths';
// Admin/configuration route (catalog + per-warehouse availability), separate
// from the daily-use board above.
export const processPathSettingsPath = '/process-paths/settings';
// Admin setup for printing scannable associate QR badges for the floor kiosk.
export const processPathAssociatesSetupPath = '/process-paths/associates/setup';
// Public (unauthenticated) employee self-assign screen — generic link with the
// associate's badge identity (their uuid id) in the query string, e.g.
// /floor?userId=<uuid>.
export const processPathSelfAssignPublicPath = '/floor';
// Public (unauthenticated) compact handheld (Honeywell CT60) process-path
// switch screen. SHARED devices: identity is ephemeral (badge scan), cleared on
// return-to-RFsmart and on idle. Optional deep-link seed: /scanner?userId=<uuid>.
export const processPathSwitchPublicPath = '/scanner';
// Ops authoring screen for date-scoped delivery overrides, shown to operators as
// "Delivery date exceptions". Flag-gated; the sidebar entry follows the same flag.
export const deliveryOverridesPath = '/delivery-overrides';
export const deliveryOverridesCreatePath = '/delivery-overrides/new';
export const driverSafetyAcknowledgementsDetailPath =
  '/acknowledgements/:acknowledgementId';
export const getDriverSafetyAcknowledgementDetailPath = (
  acknowledgementId: string,
): string => `${driverSafetyAcknowledgementsPath}/${acknowledgementId}`;

// Drivers only use Putaway here, so they see just that link. Everyone else
// gets the links their roles and flags allow.
const NavLinks = (): JSX.Element => {
  const selectedWarehouseId = useAppSelector(selectedWarehouseIdSelector);
  const {
    deliveryServiceShowAnalyticsPage,
    deliveryServiceFulfillmentDeltasPage,
    deliveryServiceShowPackingListPage,
    deliveryServiceDriverSafetyAcknowledgementsPage,
    deliveryServiceVehicleInspectionReportsPage,
    deliveryServiceDeliveryOverridesPage,
    deliveryServiceVendorReceivingPage,
    deliveryServicePutawayReportPage,
    deliveryServicePutawayApp,
    deliveryServiceInvoiceByDeliveredQty,
    deliveryServiceOptimizationScenarioComparison,
    deliveryServiceOptimizationScenarioManagement,
  } = useFlags();
  const showWarehouseLayout = useWarehouseLayoutBuilderEnabled();
  const showInvoiceByDelivery = isWarehouseFlagEnabled(
    deliveryServiceInvoiceByDeliveredQty,
    selectedWarehouseId,
  );
  const showStageGate = useOutboundGatesEnabled();
  const { user } = useAuth0();
  const showPutawayApp = hasPutawayAppAccess(
    user,
    deliveryServicePutawayApp,
    selectedWarehouseId,
  );
  const putawayLink = (
    <NavLink
      selectedIcon={<Box />}
      unselectedIcon={<Box />}
      label="Putaway"
      path={putawayAppPath}
      matchSubpaths
    />
  );

  if (isPutawayOnlyUser(user)) return <div>{putawayLink}</div>;

  return (
    <div>
      <NavLink
        selectedIcon={<IconDeliveryFilled height={24} width={24} />}
        unselectedIcon={<IconDeliveryLine height={24} width={24} />}
        label="Deliveries"
        path={deliveryPath}
      />
      <NavLink
        selectedIcon={
          <div sx={issuesSymbol}>
            <IssueSelected />
          </div>
        }
        unselectedIcon={
          <div sx={issuesSymbol}>
            <IssueUnselected />
          </div>
        }
        label="Issues"
        path={issuesPath}
      />
      {showInvoiceByDelivery && (
        <NavLink
          selectedIcon={<IconPartiallyDelivered height={24} width={24} />}
          unselectedIcon={<IconPartiallyDelivered height={24} width={24} />}
          label="Invoice Off Delivery"
          path={invoiceByDeliveryPath}
          matchSubpaths
        />
      )}
      {deliveryServiceShowAnalyticsPage && (
        <NavLink
          selectedIcon={<IconVerticalBarChartFilled height={24} width={24} />}
          unselectedIcon={<IconVerticalBarChartLine height={24} width={24} />}
          label="Analytics"
          path={analyticsPath}
        />
      )}
      <NavLink
        selectedIcon={
          <div sx={warehouseOperationsSymbol}>
            <WarehouseOperationsSelected />
          </div>
        }
        unselectedIcon={
          <div sx={warehouseOperationsSymbol}>
            <WarehouseOperationsUnselected />
          </div>
        }
        label="Past Performance"
        path={warehouseOperationsPath}
      />
      {(deliveryServiceOptimizationScenarioComparison ||
        deliveryServiceOptimizationScenarioManagement) && (
        <NavLink
          selectedIcon={
            <div sx={warehouseOperationsSymbol}>
              <TuningControls />
            </div>
          }
          unselectedIcon={
            <div sx={warehouseOperationsSymbol}>
              <TuningControls />
            </div>
          }
          label="Optimization"
          path={optimizationPath}
          matchSubpaths
        />
      )}
      {deliveryServicePutawayReportPage && (
        <NavLink
          selectedIcon={<Box />}
          unselectedIcon={<Box />}
          label="Putaway Report"
          path={putawayReportPath}
        />
      )}
      {showPutawayApp && putawayLink}
      {deliveryServiceVendorReceivingPage && (
        <NavLink
          selectedIcon={
            <div sx={warehouseOperationsSymbol}>
              <WarehouseOperationsSelected />
            </div>
          }
          unselectedIcon={
            <div sx={warehouseOperationsSymbol}>
              <WarehouseOperationsUnselected />
            </div>
          }
          label="Vendor Receiving"
          path={vendorReceivingPath}
        />
      )}
      {showStageGate && (
        <NavLink
          selectedIcon={<IconCheckedSquare height={24} width={24} />}
          unselectedIcon={<IconCheckedSquare height={24} width={24} />}
          label="Stage gate"
          path={stageGatePath}
        />
      )}
      <NavLink
        selectedIcon={<MapPinSelected />}
        unselectedIcon={<MapPinUnselected />}
        label="Locations"
        path={locationsPath}
      />
      {deliveryServiceFulfillmentDeltasPage && (
        <NavLink
          selectedIcon={<WarningOctagonFilled />}
          unselectedIcon={<WarningOctagon />}
          label="Fulfillment Deltas"
          path={fulfillmentDeltasPath}
        />
      )}
      <NavLink
        selectedIcon={<SearchFilled />}
        unselectedIcon={<Search />}
        label="Item Search"
        path={itemSearchPath}
      />
      {deliveryServiceShowPackingListPage && (
        <NavLink
          selectedIcon={<Box />}
          unselectedIcon={<Box />}
          label="Packing List"
          path={packingListPath}
        />
      )}
      <NavLink
        selectedIcon={<WarehouseNotes />}
        unselectedIcon={<WarehouseNotes />}
        label="Delivery Notes"
        path={deliveryNotesPath}
      />
      <NavLink
        selectedIcon={<Box />}
        unselectedIcon={<Box />}
        label="Dock Bookings"
        path={dockBookingsPath}
        matchSubpaths
      />
      <NavLink
        selectedIcon={
          <div sx={warehouseOperationsSymbol}>
            <WarehouseOperationsSelected />
          </div>
        }
        unselectedIcon={
          <div sx={warehouseOperationsSymbol}>
            <WarehouseOperationsUnselected />
          </div>
        }
        label="Process Paths"
        path={processPathsPath}
      />
      {showWarehouseLayout && (
        <NavLink
          selectedIcon={<Box />}
          unselectedIcon={<Box />}
          label="Warehouse Layout"
          path={warehouseLayoutPath}
          matchSubpaths
        />
      )}
      {deliveryServiceDriverSafetyAcknowledgementsPage && (
        <NavLink
          selectedIcon={<IconEdit3 height={24} width={24} />}
          unselectedIcon={<IconEdit3 height={24} width={24} />}
          label="Safety Acknowledgements"
          path={driverSafetyAcknowledgementsPath}
          matchSubpaths
        />
      )}
      {deliveryServiceVehicleInspectionReportsPage && (
        <NavLink
          selectedIcon={<IconEdit3 height={24} width={24} />}
          unselectedIcon={<IconEdit3 height={24} width={24} />}
          label="Vehicle Inspections"
          path={vehicleInspectionReportsPath}
          matchSubpaths
        />
      )}
      {/* The same flag as the route's gate (flagGatedRoute in App.tsx). */}
      {deliveryServiceDeliveryOverridesPage && (
        <NavLink
          selectedIcon={<IconCalendar3 height={24} width={24} />}
          unselectedIcon={<IconCalendar3 height={24} width={24} />}
          label="Delivery Date Exceptions"
          path={deliveryOverridesPath}
          matchSubpaths
        />
      )}
    </div>
  );
};

// A modal dialog: the browser handles Escape, keeps Tab inside, makes the page
// behind it inert and hands focus back to the opener on close. Phones get the
// whole screen with a Back bar; wider screens keep the drawer, X and click-away
// strip.
const NavMenu = ({ isPhone }: { isPhone: boolean }): JSX.Element => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const dispatch = useAppDispatch();

  const closeMenu = (): void => {
    dispatch(collapseNav());
  };

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const opener = document.activeElement;

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      // jsdom has no showModal(). Open inline instead.
      dialog.setAttribute('open', '');
    }
    // showModal() picks the first focusable area, which on wider screens is
    // the scrolling panel itself in Chrome. Start on the first control.
    dialog.querySelector<HTMLElement>('a[href], button')?.focus();

    return () => {
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      // close() restores focus itself; the fallback needs a hand.
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, []);

  return (
    <dialog
      id="navbar"
      ref={dialogRef}
      aria-label="Menu"
      onCancel={(event): void => {
        event.preventDefault();
        closeMenu();
      }}
      sx={{ ...page, flexDirection: isPhone ? 'column' : 'row' }}
    >
      {isPhone && (
        <div sx={topBarStyle}>
          <button type="button" sx={backButtonStyle} onClick={closeMenu}>
            Back
          </button>
        </div>
      )}
      <div sx={isPhone ? phoneMenuContainerStyle : menuContainerStyle}>
        <div sx={innerContainerStyle}>
          <div>
            <div id="navHeader" sx={headerContainerStyle}>
              <OdekoIcon />
              <span sx={header}>Route Management</span>
            </div>
            <NavLinks />
          </div>
          <div>
            <HelpCenter />
            <Logout />
          </div>
        </div>
        {!isPhone && (
          <button
            type="button"
            id="exitButton"
            aria-label="Close menu"
            sx={exitContainer}
            onClick={closeMenu}
          >
            <IconClose width={24} height={24} />
          </button>
        )}
      </div>
      {!isPhone && <div id="exitSpace" sx={notMenuStyle} onClick={closeMenu} />}
    </dialog>
  );
};

const Navbar = (): JSX.Element | null => {
  const isNavCollapsed = useAppSelector(isNavCollapsedSelector);
  // useBreakpointIndex starts at 0 and settles in an effect. Read here, where
  // it stays mounted, so the menu opens in the right layout and showModal's
  // focus lands on a control that stays.
  const isPhone = useBreakpointIndex() === 0;

  if (isNavCollapsed) return null;
  return <NavMenu isPhone={isPhone} />;
};

export default Navbar;
